import React, { useState, useEffect, useRef, useMemo } from "react";
import { collection, query, orderBy, limit, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import Logo from "../images/Logo.svg";

/**
 * @typedef {Object} MetricsData
 * @property {number} daysInLast365
 * @property {Date|{seconds: number, nanoseconds: number}} lastUpdated
 * @property {boolean} syncedFromBigQuery
 * @property {number} totalCaloriesYTD
 * @property {number} totalDatapointsYTD
 * @property {number} totalDistanceYTD
 * @property {number} totalHoursYTD
 * @property {number} totalWorkoutsYTD
 */

/**
 * Animated Number Component
 * @param {Object} props
 * @param {number} props.value - The target value to animate to
 * @param {Function} props.formatter - Function to format the number
 * @param {number} props.duration - Animation duration in milliseconds (default: 2000)
 */
const AnimatedNumber = ({
  value,
  formatter = (n) => n.toLocaleString(),
  duration = 2000,
}) => {
  const [displayValue, setDisplayValue] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationRef = useRef(null);
  const prevValueRef = useRef(value);

  useEffect(() => {
    if (prevValueRef.current === value) return;

    const startValue = prevValueRef.current;
    const endValue = value;
    const startTime = Date.now();

    setIsAnimating(true);

    const animate = () => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);

      const currentValue = startValue + (endValue - startValue) * easeOutCubic;
      setDisplayValue(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
        prevValueRef.current = value;
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [value, duration]);

  return (
    <span
      className={`inline-block transition-all duration-300 ${
        isAnimating ? "animate-pulse" : ""
      }`}
    >
      {formatter(Math.floor(displayValue))}
    </span>
  );
};

const DashboardStats = ({ position = 'left' }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  /** @type {[MetricsData|null, Function]} */
  const [metricsData, setMetricsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liveCounters, setLiveCounters] = useState({
    workouts: 0,
    calories: 0,
    distance: 0,
    hours: 0,
    datapoints: 0,
  });
  const [currentDay, setCurrentDay] = useState(new Date().toDateString());

  // Fetch metrics data
  const fetchLatestMetrics = async () => {
    try {
      setLoading(true);
      const metricsCollection = collection(db, "metrics");
      const q = query(
        metricsCollection,
        orderBy("lastUpdated", "desc"),
        limit(1)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        /** @type {MetricsData} */
        const latestMetric = querySnapshot.docs[0].data();
        setMetricsData(latestMetric);
      }
    } catch (error) {
      console.error("Error fetching metrics:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      const newTime = new Date();
      const newDay = newTime.toDateString();

      setCurrentTime(newTime);

      // Check if it's a new day
      if (newDay !== currentDay) {
        setCurrentDay(newDay);
        // Reset live counters
        setLiveCounters({
          workouts: 0,
          calories: 0,
          distance: 0,
          hours: 0,
          datapoints: 0,
        });
        // Fetch fresh metrics data
        fetchLatestMetrics();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [currentDay]);

  useEffect(() => {
    fetchLatestMetrics();
  }, []);

  // Live counter updates every 5 seconds
  useEffect(() => {
    if (!metricsData) return;

    const calculateRates = () => {
      const daysInPeriod = metricsData.daysInLast365;
      const secondsInPeriod = daysInPeriod * 24 * 60 * 60;
      const updateIntervalSeconds = 5;

      return {
        workoutsPerUpdate:
          (metricsData.totalWorkoutsYTD / secondsInPeriod) *
          updateIntervalSeconds,
        caloriesPerUpdate:
          (metricsData.totalCaloriesYTD / secondsInPeriod) *
          updateIntervalSeconds,
        distancePerUpdate:
          (metricsData.totalDistanceYTD / secondsInPeriod) *
          updateIntervalSeconds,
        hoursPerUpdate:
          (metricsData.totalHoursYTD / secondsInPeriod) * updateIntervalSeconds,
        datapointsPerUpdate:
          (metricsData.totalDatapointsYTD / secondsInPeriod) *
          updateIntervalSeconds,
      };
    };

    const rates = calculateRates();

    const liveTimer = setInterval(() => {
      setLiveCounters((prev) => ({
        workouts:
          prev.workouts + rates.workoutsPerUpdate * (0.7 + Math.random() * 0.6),
        calories:
          prev.calories + rates.caloriesPerUpdate * (0.7 + Math.random() * 0.6),
        distance:
          prev.distance + rates.distancePerUpdate * (0.7 + Math.random() * 0.6),
        hours: prev.hours + rates.hoursPerUpdate * (0.7 + Math.random() * 0.6),
        datapoints:
          prev.datapoints +
          rates.datapointsPerUpdate * (0.7 + Math.random() * 0.6),
      }));
    }, 5000);

    return () => clearInterval(liveTimer);
  }, [metricsData]);

  const formatTime = (date) => {
    return date.toLocaleTimeString("en-US", {
      hour12: true,
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const formatDate = (date) => {
    const day = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "long" });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const cities = [
    { name: "New York", offset: -4 },
    { name: "London", offset: 0 },
    { name: "Tokyo", offset: 9 },
    { name: "Sydney", offset: 10 },
    { name: "Los Angeles", offset: -8 },
    { name: "Dubai", offset: 4 },
  ];

  const getCityTime = (offset) => {
    const utc = currentTime.getTime() + currentTime.getTimezoneOffset() * 60000;
    const cityTime = new Date(utc + offset * 3600000);
    return formatTime(cityTime);
  };

  /**
   * @param {number|undefined} num
   * @returns {string}
   */
  const formatNumber = (num) => {
    if (!num) return "0";
    return num.toLocaleString();
  };

  /**
   * @param {number|undefined} distance
   * @returns {string}
   */
  const formatDistance = (distance) => {
    if (!distance) return "0";
    return Math.round(distance).toLocaleString();
  };

  /**
   * @param {number|undefined} hours
   * @returns {string}
   */
  const formatHours = (hours) => {
    if (!hours) return "0";
    return Math.round(hours).toLocaleString();
  };

  // Calculate live totals including estimated missing data
  const getLiveTotal = (baseValue, liveIncrement, estimatedMissing) => {
    return Math.floor((baseValue || 0) + estimatedMissing + liveIncrement);
  };

  // Calculate estimated metrics for missing time period (memoized to prevent constant recalculation)
  const estimatedMissing = useMemo(() => {
    if (!metricsData?.lastUpdated)
      return { workouts: 0, calories: 0, distance: 0, hours: 0, datapoints: 0 };

    const now = new Date();
    let lastUpdateTime;

    // Handle Firestore timestamp format
    if (metricsData.lastUpdated.seconds) {
      lastUpdateTime = new Date(metricsData.lastUpdated.seconds * 1000);
    } else {
      lastUpdateTime = new Date(metricsData.lastUpdated);
    }

    const timeDiffMs = now.getTime() - lastUpdateTime.getTime();
    const totalMissingHours = timeDiffMs / (1000 * 60 * 60);

    const daysInPeriod = metricsData.daysInLast365;
    const totalHoursInPeriod = daysInPeriod * 24;

    // Calculate hourly rates from historical data
    const hourlyRates = {
      workouts: metricsData.totalWorkoutsYTD / totalHoursInPeriod,
      calories: metricsData.totalCaloriesYTD / totalHoursInPeriod,
      distance: metricsData.totalDistanceYTD / totalHoursInPeriod,
      hours: metricsData.totalHoursYTD / totalHoursInPeriod,
      datapoints: metricsData.totalDatapointsYTD / totalHoursInPeriod,
    };

    // Return estimated totals for missing time period
    return {
      workouts: hourlyRates.workouts * totalMissingHours,
      calories: hourlyRates.calories * totalMissingHours,
      distance: hourlyRates.distance * totalMissingHours,
      hours: hourlyRates.hours * totalMissingHours,
      datapoints: hourlyRates.datapoints * totalMissingHours,
    };
  }, [metricsData]); // Only recalculate when metricsData changes

  // Two main featured metrics
  const mainMetrics = [
    {
      value: (
        <AnimatedNumber
          value={getLiveTotal(
            metricsData?.totalWorkoutsYTD,
            liveCounters.workouts,
            estimatedMissing.workouts
          )}
          formatter={formatNumber}
          duration={2000}
        />
      ),
      label: "WORKOUTS",
    },
    {
      value: (
        <AnimatedNumber
          value={getLiveTotal(
            metricsData?.totalHoursYTD,
            liveCounters.hours,
            estimatedMissing.hours
          )}
          formatter={formatHours}
          duration={2000}
        />
      ),
      label: "HOURS",
    },
  ];

  // Four smaller metrics in 2x2 grid
  const gridMetrics = [
    {
      value: (
        <AnimatedNumber
          value={getLiveTotal(
            metricsData?.totalDistanceYTD,
            liveCounters.distance,
            estimatedMissing.distance
          )}
          formatter={formatDistance}
          duration={1500}
        />
      ),
      label: "MILES MOVED",
    },
    {
      value: (
        <AnimatedNumber
          value={getLiveTotal(
            metricsData?.totalCaloriesYTD,
            liveCounters.calories,
            estimatedMissing.calories
          )}
          formatter={formatNumber}
          duration={2500}
        />
      ),
      label: "CALORIES BURNED",
    },
  ];

  // Two bottom large metrics
  const bottomMetrics = [
    {
      value: (
        <AnimatedNumber
          value={getLiveTotal(
            metricsData?.totalDatapointsYTD,
            liveCounters.datapoints,
            estimatedMissing.datapoints
          )}
          formatter={formatNumber}
          duration={3000}
        />
      ),
      label: "DATA POINTS COLLECTED",
    },
  ];

  if (loading) {
    return (
      <div className="relative h-full flex flex-col">
        <div className={`flex flex-row items-center ${position === 'right' ? 'justify-end mr-[5rem]' : 'justify-between'} mb-10 fhd:mb-20`}>
          <img src={Logo} alt="Logo" className="h-6 fhd:h-12 w-auto z-10" />
        </div>
        <div className="mb-6 fhd:mb-12 relative ml-5 fhd:ml-10">
          <h1 className="font-['Montserrat'] text-xl fhd:text-4xl font-bold text-[#FDFDFD] m-0">
            GLOBAL CARDIO DASHBOARD
          </h1>
          <div className="font-['Geist_Mono'] font-bold text-sm fhd:text-lg text-[#A18287] text-left uppercase">
            Loading metrics...
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="relative h-full flex flex-col">
        <div className={`flex flex-row items-center ${position === 'right' ? 'justify-end mr-[5rem]' : 'justify-between'} mb-10 fhd:mb-20`}>
          <img src={Logo} alt="Logo" className="h-6 fhd:h-12 w-auto z-10" />
        </div>
        <div className="mb-6 fhd:mb-12 relative ml-0">
          <h1 className="font-['Montserrat'] text-xl fhd:text-4xl font-bold text-[#FDFDFD] m-0">
            GLOBAL CARDIO DASHBOARD
          </h1>
          <div className="font-['Geist_Mono'] font-bold text-sm fhd:text-lg text-[#A18287] text-left uppercase">
            YTD | {formatDate(currentTime)} | {formatTime(currentTime)}
          </div>
        </div>

        {/* Two main featured metrics */}
        <div className="flex justify-between my-6 fhd:my-12 ml-0">
          {mainMetrics.map((metric, index) => (
            <div key={index} className="flex-1 text-start">
              <div className="font-['Montserrat'] text-3xl fhd:text-6xl font-bold text-[#FDFDFD] mb-2 leading-none">
                {metric.value}
              </div>
              <div className="font-['Montserrat'] text-[#BAA8AB] text-xs fhd:text-xl font-semibold tracking-widest">
                {metric.label}
              </div>
            </div>
          ))}
        </div>

        {/* 2x2 Grid of smaller metrics */}
        <div className="flex justify-between my-4 fhd:my-12 ml-0">
          {gridMetrics.map((metric, index) => (
            <div
              key={index}
              className="flex-1 bg-white/2 text-start transition-all duration-300 hover:bg-white/5"
            >
              <div className="font-['Montserrat'] text-3xl fhd:text-6xl font-bold text-[#FDFDFD] mb-2">
                {metric.value}
              </div>
              <div className="font-['Montserrat'] text-[#BAA8AB] mb-1 text-xs fhd:text-sm font-semibold">
                {metric.label}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom large metric */}
        <div className="flex justify-between my-4 fhd:my-8 ml-0">
          {bottomMetrics.map((metric, index) => (
            <div key={index} className="text-start transition-all duration-300">
              <div className="font-['Montserrat'] text-3xl fhd:text-6xl font-bold text-[#FDFDFD] mb-2">
                {metric.value}
              </div>
              <div className="font-['Montserrat'] text-[#BAA8AB] mb-1 text-xs fhd:text-sm font-semibold">
                {metric.label}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="fixed left-0 bottom-0 w-screen text-[#A18287] z-[1000] flex justify-between items-center px-12 pb-[30px] fhd:pb-10 font-['Geist_Mono'] text-xs fhd:text-sm">
        {cities.map((city, index) => (
          <div key={index} className="tracking-wider">
            <div className="font-['Montserrat'] text-[10px] fhd:text-xs text-[#A18287] mb-1 uppercase font-semibold text-start">
              {city.name} | {getCityTime(city.offset)}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default DashboardStats;
